import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart3,
  PieChart,
  TrendingUp,
  Users,
  Package,
  Globe,
  Truck,
  Calendar,
  Download,
  FileSpreadsheet,
  FileText,
  RefreshCw,
  ChevronRight,
  DollarSign,
  Weight,
  Building2,
  Clock,
  Filter,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle,
  CreditCard,
  Wallet,
  Receipt,
  Target,
  Percent,
  Grid3X3,
  Search,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "";

// Report Categories
const REPORT_CATEGORIES = [
  {
    id: "shipment",
    name: "Shipment Reports",
    icon: Truck,
    color: "bg-blue-500",
    reports: [
      { id: "top-customers", name: "Top 50 Customers", icon: Users },
      { id: "top-clients", name: "Top 50 Clients", icon: Building2 },
      { id: "top-products-cost", name: "Top 50 Products (Cost)", icon: Package },
      { id: "top-products-qty", name: "Top 50 Products (Qty)", icon: BarChart3 },
      { id: "date-wise", name: "Date-wise Costing", icon: Calendar },
      { id: "country-wise", name: "Country-wise Costing", icon: Globe },
      { id: "country-top-products", name: "Country Top 20 Products", icon: Globe },
      { id: "service-wise", name: "Service-wise Costing", icon: Truck },
      { id: "client-sales", name: "Client-wise Sales", icon: Building2 },
      { id: "date-service-matrix", name: "Date × Service Matrix", icon: Grid3X3 },
      { id: "summary", name: "Cost Summary", icon: DollarSign },
      { id: "weight-analysis", name: "Weight Analysis", icon: Weight },
      { id: "shipping-efficiency", name: "Shipping Efficiency", icon: Target },
      { id: "error-rate", name: "Error Rate by Client", icon: AlertTriangle },
      { id: "high-value-transactions", name: "High Value Transactions (>₹50K)", icon: ArrowUpRight },
      { id: "low-value-transactions", name: "Low Value Transactions (<₹1K)", icon: ArrowDownRight },
      { id: "api-products-costing", name: "API Products Costing", icon: Package },
      { id: "branded-products-costing", name: "Branded Products Costing", icon: Package },
    ],
  },
  {
    id: "products",
    name: "Product Reports",
    icon: Package,
    color: "bg-green-500",
    reports: [
      { id: "company-products", name: "Company-wise Products", icon: Building2 },
      { id: "generic-products", name: "Generic Name Products", icon: Package },
      { id: "category-products", name: "Category-wise Products", icon: PieChart },
      { id: "branded-products", name: "Branded vs Non-Branded", icon: Target },
      { id: "price-range", name: "Price Range Analysis", icon: DollarSign },
      { id: "new-products", name: "New Products Added", icon: TrendingUp },
    ],
  },
  {
    id: "sales",
    name: "Sales & Profit",
    icon: TrendingUp,
    color: "bg-purple-500",
    reports: [
      { id: "profit-trend", name: "Monthly Profit Trend", icon: TrendingUp },
      { id: "client-profit", name: "Client Profitability", icon: Users },
      { id: "product-profit", name: "Product Profitability", icon: Package },
      { id: "negative-profit", name: "Negative Profit Analysis", icon: AlertTriangle },
    ],
  },
  {
    id: "ledger",
    name: "Ledger Reports",
    icon: Wallet,
    color: "bg-amber-500",
    reports: [
      { id: "outstanding-balance", name: "Outstanding Balance", icon: Receipt },
      { id: "payment-history", name: "Payment History", icon: Calendar },
      { id: "credit-utilization", name: "Credit Utilization", icon: CreditCard },
    ],
  },
  {
    id: "pending",
    name: "Pending Uploads",
    icon: Clock,
    color: "bg-red-500",
    reports: [
      { id: "pending-shipment-costing", name: "Pending Shipment Costing", icon: Package, link: "/reports/pending-shipment-costing" },
      { id: "pending-sales-by-client", name: "Pending Sales by Client", icon: Users, link: "/reports/pending-sales-by-client" },
      { id: "pending-sales", name: "Pending Partner Sales", icon: Building2, link: "/reports/pending-sales" },
      { id: "pending-auto-generate", name: "Pending Auto-Generate", icon: AlertTriangle, link: "/reports/pending-auto-generate" },
    ],
  },
  {
    id: "management",
    name: "Management Reports",
    icon: Users,
    color: "bg-indigo-500",
    reports: [
      { id: "performance-reports", name: "Performance Reports (All Levels)", icon: BarChart3, link: "/reports/performance", highlight: true },
      { id: "manager-performance", name: "Manager Performance", icon: TrendingUp, link: "/reports/manager-performance" },
      { id: "sales-comparison", name: "Sales Comparison", icon: BarChart3, link: "/reports/sales-comparison" },
    ],
  },
];

// ============================================================
// GLOBAL REPORT SEARCH - Keywords for each report
// ============================================================
const REPORT_SEARCH_INDEX = [
  // Shipment Reports
  { id: "top-customers", name: "Top 50 Customers", category: "shipment", categoryName: "Shipment Reports", keywords: ["customer", "top", "best", "highest", "sales", "revenue", "client ranking"] },
  { id: "top-clients", name: "Top 50 Clients", category: "shipment", categoryName: "Shipment Reports", keywords: ["client", "top", "partner", "best", "highest", "sales"] },
  { id: "top-products-cost", name: "Top 50 Products (Cost)", category: "shipment", categoryName: "Shipment Reports", keywords: ["product", "cost", "expensive", "top", "medicine", "price"] },
  { id: "top-products-qty", name: "Top 50 Products (Qty)", category: "shipment", categoryName: "Shipment Reports", keywords: ["product", "quantity", "volume", "top", "medicine", "most sold"] },
  { id: "date-wise", name: "Date-wise Costing", category: "shipment", categoryName: "Shipment Reports", keywords: ["date", "daily", "day", "costing", "time", "calendar"] },
  { id: "country-wise", name: "Country-wise Costing", category: "shipment", categoryName: "Shipment Reports", keywords: ["country", "international", "destination", "geography", "location", "usa", "uk"] },
  { id: "country-top-products", name: "Country Top 20 Products", category: "shipment", categoryName: "Shipment Reports", keywords: ["country", "product", "top", "international", "destination"] },
  { id: "service-wise", name: "Service-wise Costing", category: "shipment", categoryName: "Shipment Reports", keywords: ["service", "shipping", "carrier", "usps", "ems", "itps", "rms"] },
  { id: "client-sales", name: "Client-wise Sales", category: "shipment", categoryName: "Shipment Reports", keywords: ["client", "sales", "partner", "revenue", "business"] },
  { id: "date-service-matrix", name: "Date × Service Matrix", category: "shipment", categoryName: "Shipment Reports", keywords: ["date", "service", "matrix", "grid", "cross", "comparison"] },
  { id: "summary", name: "Cost Summary", category: "shipment", categoryName: "Shipment Reports", keywords: ["summary", "total", "overview", "aggregate", "cost", "expense"] },
  { id: "weight-analysis", name: "Weight Analysis", category: "shipment", categoryName: "Shipment Reports", keywords: ["weight", "kg", "gram", "heavy", "light", "shipping weight"] },
  { id: "shipping-efficiency", name: "Shipping Efficiency", category: "shipment", categoryName: "Shipment Reports", keywords: ["efficiency", "performance", "shipping", "optimization", "cost per kg"] },
  { id: "error-rate", name: "Error Rate by Client", category: "shipment", categoryName: "Shipment Reports", keywords: ["error", "mistake", "issue", "problem", "client", "quality"] },
  { id: "high-value-transactions", name: "High Value Transactions (>₹50K)", category: "shipment", categoryName: "Shipment Reports", keywords: ["high", "value", "expensive", "large", "big order", "50k"] },
  { id: "low-value-transactions", name: "Low Value Transactions (<₹1K)", category: "shipment", categoryName: "Shipment Reports", keywords: ["low", "value", "cheap", "small", "1k"] },
  { id: "api-products-costing", name: "API Products Costing", category: "shipment", categoryName: "Shipment Reports", keywords: ["api", "product", "cost", "pricing", "generic"] },
  { id: "branded-products-costing", name: "Branded Products Costing", category: "shipment", categoryName: "Shipment Reports", keywords: ["branded", "brand", "product", "cost", "mrp"] },
  
  // Product Reports
  { id: "company-products", name: "Company-wise Products", category: "products", categoryName: "Product Reports", keywords: ["company", "manufacturer", "brand", "pharma", "product"] },
  { id: "generic-products", name: "Generic Name Products", category: "products", categoryName: "Product Reports", keywords: ["generic", "name", "medicine", "drug", "composition"] },
  { id: "category-products", name: "Category-wise Products", category: "products", categoryName: "Product Reports", keywords: ["category", "type", "classification", "group"] },
  { id: "branded-products", name: "Branded vs Non-Branded", category: "products", categoryName: "Product Reports", keywords: ["branded", "non-branded", "comparison", "generic"] },
  { id: "price-range", name: "Price Range Analysis", category: "products", categoryName: "Product Reports", keywords: ["price", "range", "analysis", "cost", "expensive", "cheap"] },
  { id: "new-products", name: "New Products Added", category: "products", categoryName: "Product Reports", keywords: ["new", "added", "recent", "latest", "product"] },
  
  // Sales & Profit Reports
  { id: "profit-trend", name: "Monthly Profit Trend", category: "sales", categoryName: "Sales & Profit", keywords: ["profit", "trend", "monthly", "growth", "sales", "margin"] },
  { id: "client-profit", name: "Client Profitability", category: "sales", categoryName: "Sales & Profit", keywords: ["client", "profit", "margin", "partner", "revenue"] },
  { id: "product-profit", name: "Product Profitability", category: "sales", categoryName: "Sales & Profit", keywords: ["product", "profit", "margin", "medicine", "revenue"] },
  { id: "negative-profit", name: "Negative Profit Analysis", category: "sales", categoryName: "Sales & Profit", keywords: ["negative", "loss", "deficit", "unprofitable", "problem"] },
  
  // Ledger Reports
  { id: "outstanding-balance", name: "Outstanding Balance", category: "ledger", categoryName: "Ledger Reports", keywords: ["outstanding", "balance", "due", "payment", "pending", "receivable"] },
  { id: "payment-history", name: "Payment History", category: "ledger", categoryName: "Ledger Reports", keywords: ["payment", "history", "transaction", "received", "paid"] },
  { id: "credit-utilization", name: "Credit Utilization", category: "ledger", categoryName: "Ledger Reports", keywords: ["credit", "utilization", "limit", "balance", "used"] },
  
  // Pending Uploads (with links)
  { id: "pending-shipment-costing", name: "Pending Shipment Costing", category: "pending", categoryName: "Pending Uploads", keywords: ["pending", "shipment", "costing", "upload", "incomplete"], link: "/reports/pending-shipment-costing" },
  { id: "pending-sales-by-client", name: "Pending Sales by Client", category: "pending", categoryName: "Pending Uploads", keywords: ["pending", "sales", "client", "incomplete", "upload"], link: "/reports/pending-sales-by-client" },
  { id: "pending-sales", name: "Pending Partner Sales", category: "pending", categoryName: "Pending Uploads", keywords: ["pending", "partner", "sales", "incomplete", "upload"], link: "/reports/pending-sales" },
  { id: "pending-auto-generate", name: "Pending Auto-Generate", category: "pending", categoryName: "Pending Uploads", keywords: ["pending", "auto", "generate", "automatic", "upload"], link: "/reports/pending-auto-generate" },
  
  // Management Reports (with links)
  { id: "performance-reports", name: "Performance Reports (All Levels)", category: "management", categoryName: "Management Reports", keywords: ["performance", "all", "levels", "manager", "team", "employee", "kpi"], link: "/reports/performance" },
  { id: "manager-performance", name: "Manager Performance", category: "management", categoryName: "Management Reports", keywords: ["manager", "performance", "team", "lead", "supervisor"], link: "/reports/manager-performance" },
  { id: "sales-comparison", name: "Sales Comparison", category: "management", categoryName: "Management Reports", keywords: ["sales", "comparison", "compare", "analysis", "vs"], link: "/reports/sales-comparison" },
];

// Date Range Presets
const DATE_PRESETS = [
  { id: "7d", label: "Last 7 Days", days: 7 },
  { id: "30d", label: "Last 30 Days", days: 30 },
  { id: "90d", label: "Last 90 Days", days: 90 },
  { id: "ytd", label: "Year to Date", days: -1 },
  { id: "fy", label: "Financial Year", days: -2 },
  { id: "custom", label: "Custom Range", days: 0 },
];

// Format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

// Format number
const formatNumber = (num) => {
  if (!num && num !== 0) return "0";
  return new Intl.NumberFormat("en-IN").format(num);
};

const ReportsPage = () => {
  usePageTitle("Reports & Analytics");
  const { token } = useAuth();

  // State
  const [activeCategory, setActiveCategory] = useState("shipment");
  const [activeReport, setActiveReport] = useState("top-customers");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [datePreset, setDatePreset] = useState("30d");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [limit, setLimit] = useState(50);
  const [dataAvailability, setDataAvailability] = useState(null);
  
  // Global Search State
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = useRef(null);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Global Search Function
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }
    
    const lowerQuery = query.toLowerCase();
    const results = REPORT_SEARCH_INDEX.filter(report => {
      // Match against report name
      if (report.name.toLowerCase().includes(lowerQuery)) return true;
      // Match against keywords
      if (report.keywords.some(kw => kw.toLowerCase().includes(lowerQuery))) return true;
      // Match against category name
      if (report.categoryName.toLowerCase().includes(lowerQuery)) return true;
      return false;
    }).slice(0, 10); // Limit to 10 results
    
    setSearchResults(results);
    setShowSearchDropdown(results.length > 0);
  }, []);

  // Handle report selection from search
  const handleSelectSearchResult = useCallback((report) => {
    if (report.link) {
      // External link - open in new tab
      window.open(report.link, '_blank');
    } else {
      // Internal report - switch to it
      setActiveCategory(report.category);
      setActiveReport(report.id);
    }
    setSearchQuery("");
    setSearchResults([]);
    setShowSearchDropdown(false);
  }, []);

  // Fetch data availability on mount
  useEffect(() => {
    const fetchDataAvailability = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/reports/data-availability`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setDataAvailability(data);
        }
      } catch (error) {
        console.error("Error fetching data availability:", error);
      }
    };
    if (token) {
      fetchDataAvailability();
    }
  }, [token]);

  // Auto-set date range when data availability is loaded
  useEffect(() => {
    if (dataAvailability && activeCategory === "shipment" && dataAvailability.shipment?.has_data) {
      // If current date range has no overlap with available data, suggest the available range
      const { from, to } = getDateRange();
      const availFrom = dataAvailability.shipment.date_from;
      const availTo = dataAvailability.shipment.date_to;
      
      if (availFrom && availTo && from && to) {
        // Check if there's no overlap
        if (to < availFrom || from > availTo) {
          // No overlap - data availability indicator will show
        }
      }
    }
  }, [dataAvailability, activeCategory]);

  // Calculate date range based on preset
  const getDateRange = useCallback(() => {
    const today = new Date();
    let from, to;
    to = today.toISOString().split("T")[0];

    const preset = DATE_PRESETS.find((p) => p.id === datePreset);
    if (!preset) return { from: "", to: "" };

    if (preset.days === -1) {
      // Year to Date
      from = new Date(today.getFullYear(), 0, 1).toISOString().split("T")[0];
    } else if (preset.days === -2) {
      // Financial Year (April to March)
      const month = today.getMonth();
      const year = month >= 3 ? today.getFullYear() : today.getFullYear() - 1;
      from = new Date(year, 3, 1).toISOString().split("T")[0];
    } else if (preset.days === 0) {
      // Custom
      from = dateFrom;
      to = dateTo || to;
    } else {
      from = new Date(today.getTime() - preset.days * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0];
    }

    return { from, to };
  }, [datePreset, dateFrom, dateTo]);

  // Fetch report data
  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = getDateRange();
      const params = new URLSearchParams({
        report_type: activeReport,
        category: activeCategory,
        limit: limit.toString(),
      });
      
      // Add date filters if available
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);

      const response = await fetch(
        `${BACKEND_URL}/api/reports/generate?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
        
        // Auto-apply available date range if no data in current range but data exists
        if ((!data.data || data.data.length === 0) && dataAvailability?.[activeCategory]?.has_data) {
          const categoryData = dataAvailability[activeCategory];
          if (categoryData?.date_from && categoryData?.date_to) {
            // Check if selected range doesn't overlap with available data
            if (to < categoryData.date_from || from > categoryData.date_to) {
              // Auto-apply the available date range
              setDatePreset("custom");
              setDateFrom(categoryData.date_from);
              setDateTo(categoryData.date_to);
              toast.info(`Adjusted date range to available data (${categoryData.date_from} to ${categoryData.date_to})`);
            }
          }
        }
      } else {
        toast.error("Failed to generate report");
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error("Failed to generate report");
    } finally {
      setLoading(false);
    }
  }, [token, activeReport, activeCategory, limit, getDateRange, dataAvailability]);

  // Fetch report on changes
  useEffect(() => {
    if (token) {
      fetchReport();
    }
  }, [token, activeReport, datePreset, fetchReport]);

  // Export report
  const handleExport = async (format) => {
    setExportLoading(true);
    try {
      const { from, to } = getDateRange();
      const params = new URLSearchParams({
        report_type: activeReport,
        category: activeCategory,
        limit: limit.toString(),
        format: format,
      });
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);

      const response = await fetch(
        `${BACKEND_URL}/api/reports/export?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `report_${activeReport}_${new Date().toISOString().split("T")[0]}.${format === "excel" ? "xlsx" : "pdf"}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success(`Report exported as ${format.toUpperCase()}`);
      } else {
        toast.error("Failed to export report");
      }
    } catch (error) {
      console.error("Error exporting report:", error);
      toast.error("Failed to export report");
    } finally {
      setExportLoading(false);
    }
  };

  // Get current report info
  const getCurrentReport = () => {
    const category = REPORT_CATEGORIES.find((c) => c.id === activeCategory);
    if (!category) return null;
    return category.reports.find((r) => r.id === activeReport);
  };

  // Render summary cards
  const renderSummaryCards = () => {
    if (!reportData?.summary) return null;
    const { summary } = reportData;

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {summary.total_cost !== undefined && (
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 opacity-80" />
              <span className="text-sm opacity-80">Total Cost</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(summary.total_cost)}</p>
          </div>
        )}
        {summary.total_med_cost !== undefined && (
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-5 h-5 opacity-80" />
              <span className="text-sm opacity-80">Total Med Cost</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(summary.total_med_cost)}</p>
          </div>
        )}
        {summary.total_ship_cost !== undefined && (
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Truck className="w-5 h-5 opacity-80" />
              <span className="text-sm opacity-80">Total Ship Cost</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(summary.total_ship_cost)}</p>
          </div>
        )}
        {summary.total_records !== undefined && (
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-5 h-5 opacity-80" />
              <span className="text-sm opacity-80">Total Records</span>
            </div>
            <p className="text-2xl font-bold">{formatNumber(summary.total_records)}</p>
          </div>
        )}
        {summary.total_quantity !== undefined && (
          <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-5 h-5 opacity-80" />
              <span className="text-sm opacity-80">Total Quantity</span>
            </div>
            <p className="text-2xl font-bold">{formatNumber(summary.total_quantity)}</p>
          </div>
        )}
        {summary.total_weight !== undefined && (
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Weight className="w-5 h-5 opacity-80" />
              <span className="text-sm opacity-80">Total Weight</span>
            </div>
            <p className="text-2xl font-bold">{formatNumber(Math.round(summary.total_weight / 1000))} kg</p>
          </div>
        )}
      </div>
    );
  };

  // Render bar chart (simple CSS-based)
  const renderBarChart = () => {
    if (!reportData?.data || reportData.data.length === 0) return null;
    
    // Determine the primary value key based on report type and available data
    const getPrimaryValue = (item) => {
      // Check various possible value keys in order of preference
      // For sales/profit reports
      if (item.total_profit !== undefined) return item.total_profit;
      if (item.total_sell !== undefined) return item.total_sell;
      if (item.profit !== undefined) return item.profit;
      // For shipment reports
      if (item.total_ship_cost !== undefined) return item.total_ship_cost;
      if (item.total_cost !== undefined) return item.total_cost;
      if (item.total_revenue !== undefined) return item.total_revenue;
      if (item.total_weight !== undefined) return item.total_weight;
      if (item.total_orders !== undefined) return item.total_orders;
      if (item.order_count !== undefined) return item.order_count;
      if (item.error_rate !== undefined) return item.error_rate;
      // For product reports
      if (item.product_count !== undefined) return item.product_count;
      if (item.count !== undefined) return item.count;
      // For ledger reports
      if (item.balance !== undefined) return item.balance;
      if (item.outstanding !== undefined) return item.outstanding;
      if (item.total_debit !== undefined) return item.total_debit;
      if (item.total_credit !== undefined) return item.total_credit;
      // Generic
      if (item.value !== undefined) return item.value;
      if (item.quantity !== undefined) return item.quantity;
      if (item.amount !== undefined) return item.amount;
      return 0;
    };
    
    // Determine the primary label based on available data
    const getPrimaryLabel = (item) => {
      // Date/Month based reports
      if (item.month) {
        // Format month from DDMMYY to readable format
        const m = item.month;
        if (m.length === 6) {
          const day = m.substring(0, 2);
          const mon = m.substring(2, 4);
          const year = m.substring(4, 6);
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const monthIdx = parseInt(mon, 10) - 1;
          if (monthIdx >= 0 && monthIdx < 12) {
            return `${day} ${monthNames[monthIdx]} 20${year}`;
          }
        }
        return item.month;
      }
      // Service/Country based reports
      if (item.service) return item.service;
      if (item.country) return item.country;
      // Client/Customer based reports
      if (item.client_code) return item.client_code;
      if (item.customer_name) return item.customer_name;
      // Product based reports
      if (item.product_name) return item.product_name;
      if (item.product) return item.product;
      if (item.company) return item.company;
      if (item.generic_name) return item.generic_name;
      if (item.category) return item.category;
      // Ledger reports
      if (item.ledger_name) return item.ledger_name;
      if (item.name) return item.name;
      if (item.label) return item.label;
      return null;
    };
    
    // Get absolute values for percentage calculation (for negative profits)
    const values = reportData.data.map((d) => Math.abs(getPrimaryValue(d)));
    const maxValue = Math.max(...values);
    
    // Determine display format based on data type
    const isPercentage = reportData.data.some(d => d.error_rate !== undefined);
    const isWeight = reportData.data.some(d => d.total_weight !== undefined && d.total_ship_cost === undefined);
    const isCount = reportData.data.some(d => d.product_count !== undefined || (d.count !== undefined && d.balance === undefined));
    const isCurrency = reportData.data.some(d => 
      d.total_ship_cost !== undefined || 
      d.total_cost !== undefined || 
      d.profit !== undefined ||
      d.total_profit !== undefined ||
      d.total_sell !== undefined ||
      d.balance !== undefined ||
      d.outstanding !== undefined
    );
    const hasNegatives = reportData.data.some(d => getPrimaryValue(d) < 0);

    return (
      <div className="bg-white rounded-xl border p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Visual Distribution</h3>
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {reportData.data.slice(0, 20).map((item, idx) => {
            const value = getPrimaryValue(item);
            const absValue = Math.abs(value);
            const percentage = maxValue > 0 ? (absValue / maxValue) * 100 : 0;
            const label = getPrimaryLabel(item) || `Item ${idx + 1}`;
            const isNegative = value < 0;
            
            // Format value based on type
            let displayValue;
            if (isPercentage && item.error_rate !== undefined) {
              displayValue = `${item.error_rate}%`;
            } else if (isWeight && !isCurrency) {
              displayValue = `${formatNumber(value)}g`;
            } else if (isCount && !isCurrency) {
              displayValue = formatNumber(value);
            } else if (isCurrency) {
              displayValue = formatCurrency(value);
            } else {
              displayValue = formatNumber(value);
            }

            return (
              <div key={idx} className="flex items-center gap-3">
                <span className="text-xs font-medium text-slate-600 w-8">{idx + 1}.</span>
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium truncate max-w-[200px]" title={label}>
                      {label}
                    </span>
                    <span className={`text-sm font-bold ${isNegative ? 'text-red-600' : 'text-slate-700'}`}>
                      {displayValue}
                    </span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        isNegative 
                          ? 'bg-gradient-to-r from-red-400 to-red-500' 
                          : 'bg-gradient-to-r from-blue-500 to-purple-500'
                      }`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render data table
  const renderDataTable = () => {
    if (!reportData?.data || reportData.data.length === 0) {
      return (
        <div className="bg-white rounded-xl border p-8 text-center text-slate-500">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No data available for this report</p>
        </div>
      );
    }

    const columns = reportData.columns || [];

    return (
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-800 text-white">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase">#</th>
                {columns.map((col, idx) => (
                  <th
                    key={idx}
                    className={`px-4 py-3 text-xs font-semibold uppercase ${col.align === "right" ? "text-right" : "text-left"}`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reportData.data.map((row, idx) => (
                <tr
                  key={idx}
                  className={`border-b border-slate-100 hover:bg-slate-50 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}`}
                >
                  <td className="px-4 py-3 text-sm font-medium text-slate-500">{idx + 1}</td>
                  {columns.map((col, colIdx) => (
                    <td
                      key={colIdx}
                      className={`px-4 py-3 text-sm ${col.align === "right" ? "text-right font-mono" : "text-left"}`}
                    >
                      {col.type === "currency"
                        ? formatCurrency(row[col.key])
                        : col.type === "number"
                        ? formatNumber(row[col.key])
                        : col.type === "weight"
                        ? `${formatNumber(row[col.key])}g`
                        : row[col.key] || "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 bg-slate-50 border-t text-sm text-slate-500">
          Showing {reportData.data.length} of {reportData.total || reportData.data.length} records
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-72 bg-white border-r min-h-screen p-4 sticky top-0">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-500" />
              Reports
            </h2>
            <p className="text-sm text-slate-500 mt-1">Business Analytics</p>
          </div>

          {/* Global Report Search */}
          <div className="mb-6 relative" data-testid="report-global-search" ref={searchRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search all reports..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowSearchDropdown(true)}
                className="pl-9 pr-8 py-2 text-sm bg-slate-50 border-slate-200 focus:bg-white"
                data-testid="report-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSearchResults([]);
                    setShowSearchDropdown(false);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Search Results Dropdown */}
            {showSearchDropdown && searchResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 max-h-80 overflow-y-auto">
                {searchResults.map((result, idx) => (
                  <button
                    key={result.id}
                    onClick={() => handleSelectSearchResult(result)}
                    className={`w-full flex items-start gap-3 px-3 py-2.5 hover:bg-blue-50 text-left transition-colors ${
                      idx !== searchResults.length - 1 ? "border-b border-slate-100" : ""
                    }`}
                    data-testid={`search-result-${result.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-slate-800 truncate">
                        {result.name}
                        {result.link && (
                          <span className="ml-2 text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">
                            Opens New Tab
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{result.categoryName}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {result.keywords.slice(0, 4).map((kw, kwIdx) => (
                          <span key={kwIdx} className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                            {kw}
                          </span>
                        ))}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Report Categories */}
          <div className="space-y-4">
            {REPORT_CATEGORIES.map((category) => (
              <div key={category.id}>
                <button
                  onClick={() => {
                    setActiveCategory(category.id);
                    setActiveReport(category.reports[0].id);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg font-medium transition-all ${
                    activeCategory === category.id
                      ? "bg-slate-100 text-slate-900"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <div className={`p-1.5 rounded-lg ${category.color}`}>
                    <category.icon className="w-4 h-4 text-white" />
                  </div>
                  {category.name}
                </button>

                {/* Sub-reports */}
                {activeCategory === category.id && (
                  <div className="ml-4 mt-2 space-y-1 border-l-2 border-slate-200 pl-3">
                    {category.reports.map((report) => (
                      <button
                        key={report.id}
                        onClick={() => {
                          if (report.link) {
                            // Open in new tab for dedicated report pages
                            window.open(report.link, '_blank');
                          } else {
                            setActiveReport(report.id);
                          }
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                          activeReport === report.id && !report.link
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <report.icon className="w-4 h-4" />
                        {report.name}
                        {report.link ? (
                          <ArrowUpRight className="w-4 h-4 ml-auto text-slate-400" />
                        ) : activeReport === report.id ? (
                          <ChevronRight className="w-4 h-4 ml-auto" />
                        ) : null}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                {getCurrentReport()?.name || "Report"}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {datePreset !== "custom"
                  ? DATE_PRESETS.find((p) => p.id === datePreset)?.label
                  : `${dateFrom} to ${dateTo}`}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Date Preset */}
              <Select value={datePreset} onValueChange={setDatePreset}>
                <SelectTrigger className="w-[160px]">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Date Range" />
                </SelectTrigger>
                <SelectContent>
                  {DATE_PRESETS.map((preset) => (
                    <SelectItem key={preset.id} value={preset.id}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Custom Date Inputs */}
              {datePreset === "custom" && (
                <>
                  <DMYDateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[140px]" />
                  <span className="text-slate-400">to</span>
                  <DMYDateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[140px]" />
                </>
              )}

              {/* Limit */}
              <Select value={limit.toString()} onValueChange={(v) => setLimit(parseInt(v))}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue placeholder="Limit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">Top 10</SelectItem>
                  <SelectItem value="20">Top 20</SelectItem>
                  <SelectItem value="50">Top 50</SelectItem>
                  <SelectItem value="100">Top 100</SelectItem>
                </SelectContent>
              </Select>

              {/* Refresh */}
              <Button
                variant="outline"
                size="sm"
                onClick={fetchReport}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>

              {/* Export Buttons */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport("excel")}
                disabled={exportLoading || !reportData?.data?.length}
                className="gap-2"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport("pdf")}
                disabled={exportLoading || !reportData?.data?.length}
                className="gap-2"
              >
                <FileText className="w-4 h-4" />
                PDF
              </Button>
            </div>
          </div>

          {/* Data Availability Indicator */}
          {dataAvailability && (
            <div className="mb-6">
              {(() => {
                const categoryData = dataAvailability[activeCategory];
                const { from, to } = getDateRange();
                
                // Check if current filter has no data overlap
                const hasNoOverlap = categoryData?.has_data && 
                  categoryData?.date_from && categoryData?.date_to &&
                  from && to && (to < categoryData.date_from || from > categoryData.date_to);
                
                // Check if no data at all
                const noDataAtAll = !categoryData?.has_data || categoryData?.record_count === 0;
                
                if (hasNoOverlap) {
                  return (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <AlertTriangle className="w-5 h-5 text-amber-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-amber-800">No data in selected date range</h4>
                          <p className="text-sm text-amber-700 mt-1">
                            Your selected range ({from} to {to}) doesn't contain any {activeCategory} data.
                          </p>
                          <div className="mt-3 p-3 bg-white rounded-lg border border-amber-200">
                            <p className="text-sm font-medium text-slate-700 mb-2">📊 Data Available:</p>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-slate-600">
                                <strong className="text-slate-800">{formatNumber(categoryData.record_count)}</strong> records
                              </span>
                              <span className="text-slate-400">|</span>
                              <span className="text-slate-600">
                                From <strong className="text-slate-800">{categoryData.date_from}</strong> to <strong className="text-slate-800">{categoryData.date_to}</strong>
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="mt-3 text-amber-700 border-amber-300 hover:bg-amber-50"
                              onClick={() => {
                                setDatePreset("custom");
                                setDateFrom(categoryData.date_from);
                                setDateTo(categoryData.date_to);
                              }}
                            >
                              <Calendar className="w-4 h-4 mr-2" />
                              Use Available Date Range
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                if (noDataAtAll && (activeCategory === "ledger")) {
                  return (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          <Package className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-700">No {activeCategory} data available</h4>
                          <p className="text-sm text-slate-500">Upload {activeCategory} data to generate reports.</p>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                // Show data availability summary (collapsed)
                if (categoryData?.has_data && !hasNoOverlap) {
                  return (
                    <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 rounded-lg px-3 py-2 w-fit">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span>
                        {formatNumber(categoryData.record_count)} records available
                        {categoryData.date_from && categoryData.date_to && (
                          <span className="text-slate-400 ml-1">
                            ({categoryData.date_from} to {categoryData.date_to})
                          </span>
                        )}
                      </span>
                    </div>
                  );
                }
                
                return null;
              })()}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              <span className="ml-3 text-slate-500">Generating report...</span>
            </div>
          )}

          {/* Report Content */}
          {!loading && (
            <>
              {/* Summary Cards */}
              {renderSummaryCards()}

              {/* Chart */}
              {renderBarChart()}

              {/* Data Table */}
              {renderDataTable()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
